import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import RijekaButton from '../components/RijekaButton';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ImageLightbox from '../components/ImageLightbox';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY
const formatDate = (dateString) => {
  if (!dateString) return dateString;
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateString;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [roomMedia, setRoomMedia] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [selectedServices, setSelectedServices] = useState({});
  const [extraServicesOpen, setExtraServicesOpen] = useState(false);
  const [customFieldsAnswers, setCustomFieldsAnswers] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ownerInfo, setOwnerInfo] = useState(null);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeApplied, setPromoCodeApplied] = useState(null);
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  
  // Rate details state
  const [rateDetails, setRateDetails] = useState(null);

  const propertyId = searchParams.get('propertyId');
  const roomId = searchParams.get('roomId');
  const checkIn = searchParams.get('checkIn');
  const checkOut = searchParams.get('checkOut');
  const adults = parseInt(searchParams.get('adults')) || 1;
  const children = parseInt(searchParams.get('children')) || 0;
  const propertyName = searchParams.get('propertyName');
  const roomName = searchParams.get('roomName');
  const price = searchParams.get('price');
  
  // Rate info (if booking through rate)
  const rateId = searchParams.get('rateId');
  const rateName = searchParams.get('rateName') ? decodeURIComponent(searchParams.get('rateName')) : null;
  const rateDiscount = parseFloat(searchParams.get('rateDiscount')) || 0;
  const rateSurcharge = parseFloat(searchParams.get('rateSurcharge')) || 0;
  
  // Extra guest info
  const extraGuestPrice = parseFloat(searchParams.get('extraGuestPrice')) || 0;
  const extraGuestsCount = parseInt(searchParams.get('extraGuestsCount')) || 0;
  const extraGuestTotal = parseFloat(searchParams.get('extraGuestTotal')) || 0;
  
  const totalGuests = adults + children;

  // Fetch rate details if rateId is provided
  React.useEffect(() => {
    const fetchRateDetails = async () => {
      if (!rateId) return;
      
      try {
        const response = await axios.get(`${API}/rates/${rateId}`);
        setRateDetails(response.data);
      } catch (error) {
        console.error('Error fetching rate details:', error);
      }
    };

    fetchRateDetails();
  }, [rateId]);

  // Get localized long description based on current language
  const getRateLongDescription = () => {
    if (!rateDetails) return null;
    
    const langKey = `long_desc_${i18n.language}`;
    return rateDetails[langKey] || rateDetails.long_desc_en || rateDetails.long_desc_hr || null;
  };

  // Fetch room media on mount and when language changes
  React.useEffect(() => {
    const fetchRoomMedia = async () => {
      try {
        const response = await axios.get(`${API}/rooms/${roomId}/media?lang=${i18n.language}`);
        setRoomMedia(response.data);
      } catch (error) {
        console.error('Error fetching room media:', error);
        setRoomMedia({ images: [], description: null, amenities: [] });
      } finally {
        setMediaLoading(false);
      }
    };

    if (roomId) {
      fetchRoomMedia();
    }
  }, [roomId, i18n.language]); // Re-fetch when language changes

  // Fetch owner info for GDPR compliance checkbox
  React.useEffect(() => {
    const fetchOwnerInfo = async () => {
      try {
        const response = await axios.get(`${API}/owners/by-room/${roomId}`);
        setOwnerInfo(response.data);
      } catch (error) {
        console.error('Error fetching owner info:', error);
      }
    };

    if (roomId) {
      fetchOwnerInfo();
    }
  }, [roomId]);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    address: '',
    zipCode: '',
    city: '',
    country: '',
    email: '',
    phone: '',
    message: ''
  });

  // Calculate number of nights
  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const diffTime = Math.abs(checkOutDate - checkInDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const nights = calculateNights();

  // Handle extra service selection
  const handleServiceChange = (serviceIndex, value) => {
    setSelectedServices(prev => ({
      ...prev,
      [serviceIndex]: value
    }));
  };

  // Calculate extra services total
  const calculateExtraServicesTotal = () => {
    if (!roomMedia?.extra_services) return 0;
    
    let total = 0;
    roomMedia.extra_services.forEach((service, index) => {
      const selectedValue = selectedServices[index];
      if (!selectedValue) return;
      
      if (service.type === 'checkbox' && selectedValue === true) {
        // For checkbox, apply price once for entire stay
        total += service.price_per_unit;
      } else if (service.type === 'quantity' && selectedValue > 0) {
        // For quantity, multiply quantity by price (no nights multiplication)
        total += service.price_per_unit * selectedValue;
      }
    });
    
    return total;
  };

  // Calculate total price including extra services and promo discount
  // Note: 'price' from URL already includes base room price + extra guests (discounted)
  // We need to show the breakdown properly
  const totalRoomPrice = parseFloat(price) || 0; // This already includes extra guests
  const roomPriceWithoutExtras = totalRoomPrice - extraGuestTotal; // Base room price only
  const extraServicesTotal = calculateExtraServicesTotal();
  const promoDiscount = promoCodeApplied ? (totalRoomPrice * promoCodeApplied.discount_percent / 100) : 0;
  const discountedBasePrice = totalRoomPrice - promoDiscount;
  const totalPrice = discountedBasePrice + extraServicesTotal;
  
  // For backward compatibility
  const basePrice = totalRoomPrice;

  // Handle promo code validation
  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error(t('booking.invalidPromoCode'));
      return;
    }

    setPromoCodeLoading(true);
    try {
      // Validate against base price only (not extra services)
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode.trim().toUpperCase(),
        total: basePrice
      });
      
      setPromoCodeApplied(response.data);
      toast.success(`${t('booking.promoApplied')} - ${response.data.discount_percent}% ${t('booking.promoDiscount')}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking.invalidPromoCode'));
      setPromoCodeApplied(null);
    } finally {
      setPromoCodeLoading(false);
    }
  };

  // Remove promo code
  const handleRemovePromoCode = () => {
    setPromoCode('');
    setPromoCodeApplied(null);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    if (roomMedia?.images) {
      setLightboxIndex((prev) => (prev + 1) % roomMedia.images.length);
    }
  };

  const prevImage = () => {
    if (roomMedia?.images) {
      setLightboxIndex((prev) => (prev - 1 + roomMedia.images.length) % roomMedia.images.length);
    }
  };

  const handlePayment = async () => {
    // Validate required fields
    if (!formData.title || !formData.firstName || !formData.lastName || 
        !formData.email || !formData.phone || !formData.country) {
      toast.error('Please fill in all required fields (*)');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    // Validate custom fields
    if (roomMedia?.custom_fields) {
      for (let i = 0; i < roomMedia.custom_fields.length; i++) {
        const field = roomMedia.custom_fields[i];
        if (field.required && !customFieldsAnswers[i]) {
          toast.error(`Please answer: ${field.label}`);
          return;
        }
      }
    }

    setLoading(true);

    try {
      const bookingDetails = {
        property_id: propertyId,
        room_id: roomId,
        check_in: checkIn,
        check_out: checkOut,
        guests: totalGuests,
        adults: adults,
        children: children,
        title: formData.title,
        first_name: formData.firstName,
        last_name: formData.lastName,
        address: formData.address,
        zip_code: formData.zipCode,
        city: formData.city,
        country: formData.country,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        total_price: totalPrice,
        lang: i18n.language, // Save current language for thank you page
        base_price: basePrice, // Base room price without extras
        extra_services: Object.entries(selectedServices)
          .filter(([_, value]) => value)
          .map(([index, value]) => ({
            ...roomMedia.extra_services[parseInt(index)],
            selected_quantity: typeof value === 'number' ? value : 1
          })),
        custom_fields_answers: roomMedia?.custom_fields ? 
          roomMedia.custom_fields.map((field, index) => ({
            question: field.label,
            answer: customFieldsAnswers[index] || ''
          })).filter(item => item.answer) : [],
        // Promo code info
        promo_code: promoCodeApplied?.code || null,
        discount_amount: promoDiscount || null,
        // Rate info
        rate_id: rateId || null,
        rate_name: rateName || null,
        rate_discount: rateDiscount || null,
        rate_surcharge: rateSurcharge || null,
        // Extra guest info
        extra_guest_price: extraGuestPrice || null,
        extra_guests_count: extraGuestsCount || null,
        extra_guest_total: extraGuestTotal || null
      };

      const response = await axios.post(`${API}/bookings/create-checkout`, {
        booking_details: bookingDetails,
        origin_url: window.location.origin
      });

      // Save language to localStorage for thank you page loading screen
      localStorage.setItem('booking_language', i18n.language);
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Payment error:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        // Room is no longer available
        toast.error(
          error.response?.data?.detail || 
          'This room is no longer available for the selected dates. Please search again and try different dates or another room.',
          { duration: 6000 }
        );
      } else {
        // Generic error for other cases
        toast.error('Failed to initiate payment. Please try again.');
      }
      
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Unified Header - Logo Only */}
      <Header />

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <div className="max-w-6xl mx-auto pt-4 sm:pt-8">
          {/* Header with Title and Back Button */}
          <div className="flex items-center justify-between mb-6 sm:mb-12 gap-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[var(--color-text-primary)]">{t('booking.completeBooking')}</h2>
            <RijekaButton 
              variant="outline" 
              onClick={() => navigate(-1)}
              data-testid="back-button"
              className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-sm sm:text-base px-3 sm:px-4"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="hidden sm:inline">{t('booking.backToProperties')}</span>
            </RijekaButton>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
            {/* Room Information - 2 columns */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              {/* Room Images */}
              <div className="bg-white shadow-lg rounded-lg overflow-hidden" style={{boxShadow: '5px -5px 15px 5px rgba(53, 57, 60, 0.2)'}}>
                {mediaLoading ? (
                  <div className="h-64 sm:h-96 flex items-center justify-center bg-[var(--color-border)]">
                    <div className="inline-block animate-spin h-12 w-12 border-4 border-[var(--color-primary)] border-r-transparent rounded-full"></div>
                  </div>
                ) : roomMedia?.images && roomMedia.images.length > 0 ? (
                  <Carousel className="w-full">
                    <CarouselContent>
                      {roomMedia.images.map((imageUrl, index) => (
                        <CarouselItem key={index}>
                          <div className="w-full h-64 sm:h-96">
                            <img
                              src={imageUrl}
                              alt={`${roomName} - Image ${index + 1}`}
                              className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => openLightbox(index)}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%23D3D0C7"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2335393C" font-size="20">Image Not Available</text></svg>';
                              }}
                            />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    {roomMedia.images.length > 1 && (
                      <>
                        <CarouselPrevious className="left-4 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white border-0" />
                        <CarouselNext className="right-4 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white border-0" />
                      </>
                    )}
                  </Carousel>
                ) : (
                  <div className="h-96 flex items-center justify-center bg-[var(--color-border)]">
                    <div className="text-center text-[var(--color-text-primary)]">
                      <svg className="w-20 h-20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm font-medium">No images available</p>
                    </div>
                  </div>
                )}
                <div className="bg-[var(--color-primary)] text-white p-5">
                  <h3 className="text-2xl font-bold">{roomMedia?.room_name || roomName}</h3>
                  <p className="text-sm mt-1 opacity-90">{propertyName}</p>
                </div>
              </div>

              {/* Room Description and Amenities */}
              {!mediaLoading && (
                <div className="bg-white shadow-lg p-6 space-y-8 rounded-lg">
                  {/* Rate Description - displayed above room description */}
                  {getRateLongDescription() && (
                    <div className="bg-gradient-to-r from-[var(--color-primary)]/5 to-[var(--color-primary)]/10 p-5 rounded-lg border border-[var(--color-primary)]/20">
                      <div className="flex items-center gap-2 mb-3">
                        
                        <h4 className="text-lg font-bold text-[var(--color-text-primary)] uppercase tracking-wide">
                          {rateName || t('rates.rateDetails')}
                        </h4>
                        {rateDiscount > 0 && (
                          <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded">
                            -{rateDiscount}%
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700 leading-relaxed">{getRateLongDescription()}</p>
                    </div>
                  )}
                  
                  {roomMedia?.description && (
                    <div>
                      <h4 className="text-lg font-bold text-[var(--color-text-primary)] mb-4 uppercase tracking-wide">{t('properties.description')}</h4>
                      <p className="text-gray-700 leading-relaxed">{roomMedia.description}</p>
                    </div>
                  )}
                  
                  {/* All Amenities with Icons - Clean grid without background */}
                  {(() => {
                    const langKey = `amenities_with_icons_${i18n.language}`;
                    const amenitiesWithIcons = roomMedia?.[langKey] || roomMedia?.amenities_with_icons_hr || [];
                    
                    if (amenitiesWithIcons.length > 0) {
                      return (
                        <div>
                          <h4 className="text-lg font-bold text-[var(--color-text-primary)] mb-4 uppercase tracking-wide">{t('properties.amenities')}</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3">
                            {amenitiesWithIcons.map((amenity, index) => (
                              <div 
                                key={index}
                                className="flex items-center gap-2 text-xs text-[var(--color-text-primary)] font-medium"
                              >
                                {amenity.icon_url ? (
                                  <img src={amenity.icon_url} alt="" className="w-8 h-8 object-contain flex-shrink-0 mt-0.5" />
                                ) : (
                                  <svg className="w-5 h-5 flex-shrink-0 text-[var(--color-primary)] mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                                <span className="leading-tight">{amenity.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    
                    // Fallback to legacy amenities
                    if (roomMedia?.amenities && roomMedia.amenities.length > 0) {
                      return (
                        <div>
                          <h4 className="text-lg font-bold text-[var(--color-text-primary)] mb-4 uppercase tracking-wide">{t('properties.amenities')}</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-6 gap-y-3">
                            {roomMedia.amenities.map((amenity, index) => (
                              <div 
                                key={index}
                                className="flex items-start gap-2 text-sm text-[var(--color-text-primary)] font-medium"
                              >
                                <svg className="w-5 h-5 flex-shrink-0 text-[var(--color-primary)] mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="leading-tight">{amenity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    
                    return null;
                  })()}
                </div>
              )}
            </div>

            {/* Booking Summary and Guest Form - 1 column */}
            <div className="lg:col-span-1 space-y-6">
              {/* Booking Summary */}
              <div className="bg-white shadow-lg sticky top-8 rounded-lg overflow-hidden" data-testid="booking-summary">
                <div className="bg-[var(--color-dark)] text-white p-5">
                  <h3 className="text-xl font-bold">{t('booking.bookingSummary')}</h3>
                </div>
                <div className="p-5 space-y-4">
                  <div className="border-b border-[var(--color-border)] pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">{t('booking.checkIn')}</p>
                    <p className="font-bold text-base text-[var(--color-text-primary)]">{formatDate(checkIn)}</p>
                  </div>
                  <div className="border-b border-[var(--color-border)] pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">{t('booking.checkOut')}</p>
                    <p className="font-bold text-base text-[var(--color-text-primary)]">{formatDate(checkOut)}</p>
                  </div>
                  <div className="border-b border-[var(--color-border)] pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">{t('booking.totalNights')}</p>
                    <p className="font-bold text-base text-[var(--color-text-primary)]">{nights} {nights > 1 ? t('properties.nights') : t('properties.night')}</p>
                  </div>
                  <div className="border-b border-[var(--color-border)] pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">{t('booking.guests')}</p>
                    <p className="font-bold text-base text-[var(--color-text-primary)]">{totalGuests} ({adults} {adults > 1 ? t('home.adults') : t('home.adult')}, {children} {children !== 1 ? t('home.children') : t('home.child')})</p>
                  </div>
                  
                  {/* Rate Applied Info */}
                  {rateName && (
                    <div className="border-b border-[var(--color-border)] pb-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 font-semibold">{t('rates.rateApplied')}</p>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-base text-[var(--color-text-primary)]">{rateName}</p>
                        {rateDiscount > 0 && (
                          <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">
                            -{rateDiscount}%
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Promo Code Input */}
                  <div className="border-b border-[var(--color-border)] pb-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-semibold">{t('booking.promoCode')}</p>
                    {promoCodeApplied ? (
                      <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div>
                          <span className="font-mono font-bold text-green-700">{promoCodeApplied.code}</span>
                          <span className="ml-2 text-green-600 text-sm">-{promoCodeApplied.discount_percent}%</span>
                        </div>
                        <button
                          onClick={handleRemovePromoCode}
                          className="text-red-500 hover:text-red-700 text-sm font-medium"
                          data-testid="remove-promo-code"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder={t('booking.promoCodePlaceholder')}
                          className="flex-1 h-10 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)] font-mono uppercase"
                          data-testid="promo-code-input"
                          onKeyDown={(e) => e.key === 'Enter' && handleApplyPromoCode()}
                        />
                        <button
                          onClick={handleApplyPromoCode}
                          disabled={promoCodeLoading || !promoCode.trim()}
                          className="px-4 h-10 bg-[var(--color-primary)] text-white font-semibold rounded-md hover:bg-[var(--color-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                          data-testid="apply-promo-code"
                        >
                          {promoCodeLoading ? '...' : t('booking.applyCode')}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="pt-2 space-y-2">
                    {/* Room price (without extra guests) */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {t('booking.roomPrice', 'Cijena sobe')} ({nights} {nights === 1 ? t('properties.night') : t('properties.nights')})
                      </span>
                      <span className="font-semibold">€{roomPriceWithoutExtras.toFixed(2)}</span>
                    </div>
                    {/* Extra guest charge breakdown */}
                    {extraGuestTotal > 0 && (
                      <div className="flex justify-between items-center text-sm bg-amber-50 -mx-4 px-4 py-1.5 border-y border-amber-100">
                        <span className="text-amber-700">
                          {t('booking.extraGuestCharge', 'Dodatni gost')} ({extraGuestsCount} × €{extraGuestPrice} × {nights} {nights === 1 ? t('properties.night') : t('properties.nights')})
                        </span>
                        <span className="font-semibold text-amber-700">€{extraGuestTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {extraServicesTotal > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">{t('booking.extraServices')}</span>
                        <span className="font-semibold text-[var(--color-primary)]">€{extraServicesTotal.toFixed(2)}</span>
                      </div>
                    )}
                    {promoCodeApplied && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-green-600">{t('booking.promoDiscount')} ({promoCodeApplied.discount_percent}%)</span>
                        <span className="font-semibold text-green-600">-€{promoDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-[var(--color-border)] pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{t('booking.totalPrice')}</p>
                        <p className="text-3xl font-bold text-[var(--color-primary)]">€{totalPrice.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Extra Services Section - Collapsible - Only show if there are services */}
          {roomMedia?.extra_services && roomMedia.extra_services.length > 0 && (
            <div className="mt-8">
              <div className="bg-white shadow-lg overflow-hidden rounded-lg">
                {/* Header - Always Visible */}
                <div 
                  className="bg-[var(--color-dark)] text-white p-6 cursor-pointer hover:bg-[var(--color-dark-hover)] transition-colors"
                  onClick={() => setExtraServicesOpen(!extraServicesOpen)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-2xl font-bold">{t('booking.extraServicesTitle')}</h3>
                      <p className="text-sm mt-1 opacity-90">{t('booking.extraServicesSubtitle')}</p>
                    </div>
                    <svg 
                      className={`w-6 h-6 transition-transform ${extraServicesOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                
                {/* Collapsible Content */}
                {extraServicesOpen && (
                  <div className="p-4 sm:p-6 space-y-4 border-t border-gray-200">
                    {roomMedia.extra_services.map((service, index) => (
                      <div key={index} className="border border-[var(--color-border)] p-3 sm:p-4 rounded-lg hover:border-[var(--color-primary)] transition-all">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h4 className="font-bold text-sm sm:text-base text-[var(--color-text-primary)]">{service.name}</h4>
                              <span className="text-xs px-2 py-0.5 bg-[var(--color-primary)] text-white rounded-full whitespace-nowrap">
                                €{service.price_per_unit.toFixed(2)}
                              </span>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 mb-2">{service.description}</p>
                            
                            {service.type === 'quantity' && (
                              <p className="text-xs text-gray-500">
                                Min: {service.min_quantity}, Max: {service.max_is_nights ? `${nights} (nights)` : service.max_quantity}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex-shrink-0 self-start sm:self-start">
                            {service.type === 'checkbox' ? (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedServices[index] || false}
                                  onChange={(e) => handleServiceChange(index, e.target.checked)}
                                  className="w-5 h-5"
                                  style={{accentColor: 'var(--color-primary)'}}
                                />
                                <span className="text-sm font-medium">Add</span>
                              </label>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    const current = selectedServices[index] || 0;
                                    const newValue = Math.max(0, current - 1);
                                    handleServiceChange(index, newValue === 0 ? null : newValue);
                                  }}
                                  className="w-8 h-8 flex items-center justify-center bg-[var(--color-border)] hover:bg-[var(--color-primary)] hover:text-white transition-all font-bold rounded"
                                  disabled={(selectedServices[index] || 0) <= 0}
                                >
                                  −
                                </button>
                                <span className="w-12 text-center font-bold">
                                  {selectedServices[index] || 0}
                                </span>
                                <button
                                  onClick={() => {
                                    const current = selectedServices[index] || 0;
                                    const maxQty = service.max_is_nights ? nights : service.max_quantity;
                                    const newValue = Math.min(maxQty, current + 1);
                                    handleServiceChange(index, newValue);
                                  }}
                                  className="w-8 h-8 flex items-center justify-center bg-[var(--color-border)] hover:bg-[var(--color-primary)] hover:text-white transition-all font-bold rounded"
                                  disabled={(selectedServices[index] || 0) >= (service.max_is_nights ? nights : service.max_quantity)}
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {selectedServices[index] && (
                          <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">
                                {service.type === 'quantity' 
                                  ? `${selectedServices[index]} × €${service.price_per_unit.toFixed(2)}`
                                  : `€${service.price_per_unit.toFixed(2)} (one-time)`
                                }
                              </span>
                              <span className="font-bold text-[var(--color-primary)]">
                                €{(
                                  service.type === 'quantity'
                                    ? service.price_per_unit * selectedServices[index]
                                    : service.price_per_unit
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Guest Details Form - Full Width Below */}
          <div className="mt-8">
            <div className="bg-white shadow-lg rounded-lg overflow-hidden" data-testid="guest-details-form">
              <div className="bg-[var(--color-primary)] text-white p-6">
                <h3 className="text-2xl font-bold">{t('booking.guestDetails')}</h3>
                <p className="text-sm mt-1 opacity-90">{t('booking.fillAllFields')}</p>
              </div>
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <Label htmlFor="title" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.title')} *</Label>
                    <select
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="w-full h-12 px-4 border-2 border-[var(--color-border)] focus:outline-none focus:border-[var(--color-primary)] text-base font-medium"
                      data-testid="title-select"
                    >
                      <option value="">{t('booking.selectTitle')}</option>
                      <option value="Mr">{t('booking.mr')}</option>
                      <option value="Ms">{t('booking.ms')}</option>
                      <option value="Mrs">{t('booking.mrs')}</option>
                      <option value="Dr">{t('booking.dr')}</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="firstName" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.firstName')} *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="John"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.lastName')} *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="email" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.phone')} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="md:col-span-3">
                    <Label htmlFor="address" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.address')}</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.city')}</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="New York"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zipCode" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.zipCode')}</Label>
                    <Input
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="12345"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="zip-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.country')} *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="United States"
                      className="h-12 border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                      data-testid="country-input"
                    />
                  </div>
                </div>

                {/* Custom Fields */}
                {roomMedia?.custom_fields && roomMedia.custom_fields.length > 0 && (
                  <div className="mb-6 border-t border-[var(--color-border)] pt-6">
                    <h4 className="text-sm font-bold text-[var(--color-text-primary)] uppercase tracking-wide mb-4">{t('booking.additionalInfo')}</h4>
                    {roomMedia.custom_fields.map((field, index) => (
                      <div key={index} className="mb-4">
                        <Label className="text-sm font-semibold text-[var(--color-text-primary)] mb-2 flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-600">*</span>}
                        </Label>
                        {field.type === 'text' ? (
                          <Input
                            value={customFieldsAnswers[index] || ''}
                            onChange={(e) => setCustomFieldsAnswers({...customFieldsAnswers, [index]: e.target.value})}
                            placeholder={t('booking.enterAnswer')}
                            required={field.required}
                            className="w-full border-2 border-[var(--color-border)] focus:border-[var(--color-primary)]"
                          />
                        ) : (
                          <select
                            value={customFieldsAnswers[index] || ''}
                            onChange={(e) => setCustomFieldsAnswers({...customFieldsAnswers, [index]: e.target.value})}
                            required={field.required}
                            className="w-full px-4 py-3 border-2 border-[var(--color-border)] focus:outline-none focus:border-[var(--color-primary)]"
                          >
                            <option value="">{t('booking.selectOption')}</option>
                            {field.options.map((option, optIdx) => (
                              <option key={optIdx} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="mb-6">
                  <Label htmlFor="message" className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wide mb-2">{t('booking.message')}</Label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t('booking.messagePlaceholder')}
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-[var(--color-border)] focus:outline-none focus:border-[var(--color-primary)] resize-none"
                    data-testid="message-input"
                  />
                </div>

                {/* Terms & Privacy Acceptance Checkbox */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                      data-testid="terms-checkbox"
                    />
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)', fontFamily: 'var(--font-body)' }}>
                      {i18n.language === 'hr' ? (
                        <>
                          Prihvaćam{' '}
                          <Link to={`/privacy-policy?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Politiku Privatnosti
                          </Link>
                          {' '}i{' '}
                          <Link to={`/terms-and-conditions?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Uvjete Korištenja
                          </Link>
                          {' '}za objekt: <strong>{ownerInfo?.room_name || roomName || 'N/A'}</strong>
                        </>
                      ) : i18n.language === 'de' ? (
                        <>
                          Ich akzeptiere die{' '}
                          <Link to={`/privacy-policy?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Datenschutzerklärung
                          </Link>
                          {' '}und die{' '}
                          <Link to={`/terms-and-conditions?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Allgemeinen Geschäftsbedingungen
                          </Link>
                          {' '}für die Unterkunft: <strong>{ownerInfo?.room_name || roomName || 'N/A'}</strong>
                        </>
                      ) : i18n.language === 'it' ? (
                        <>
                          Accetto la{' '}
                          <Link to={`/privacy-policy?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Informativa sulla Privacy
                          </Link>
                          {' '}e i{' '}
                          <Link to={`/terms-and-conditions?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Termini e Condizioni
                          </Link>
                          {' '}per la struttura: <strong>{ownerInfo?.room_name || roomName || 'N/A'}</strong>
                        </>
                      ) : (
                        <>
                          I accept the{' '}
                          <Link to={`/privacy-policy?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Privacy Policy
                          </Link>
                          {' '}and{' '}
                          <Link to={`/terms-and-conditions?roomId=${roomId}`} target="_blank" className="text-[var(--color-primary)] hover:underline font-medium">
                            Terms and Conditions
                          </Link>
                          {' '}for property: <strong>{ownerInfo?.room_name || roomName || 'N/A'}</strong>
                        </>
                      )}
                    </span>
                  </label>
                </div>

                <div className="pt-6">
                  <RijekaButton 
                    onClick={handlePayment} 
                    className="w-full py-6 text-lg font-bold"
                    disabled={loading || !termsAccepted}
                    data-testid="proceed-payment-button"
                  >
                    {loading ? t('common.loading').toUpperCase() : t('booking.proceedToPayment').toUpperCase()}
                  </RijekaButton>
                  {!termsAccepted && (
                    <p className="text-center text-sm text-amber-600 mt-2">
                      {i18n.language === 'hr' ? 'Morate prihvatiti uvjete korištenja za nastavak' :
                       i18n.language === 'de' ? 'Sie müssen die Bedingungen akzeptieren, um fortzufahren' :
                       i18n.language === 'it' ? 'Devi accettare i termini per procedere' :
                       'You must accept the terms to proceed'}
                    </p>
                  )}
                  <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-600">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="font-medium">{t('booking.securePayment')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
      
      {/* Image Lightbox */}
      {lightboxOpen && roomMedia?.images && roomMedia.images.length > 0 && (
        <ImageLightbox
          images={roomMedia.images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}
    </div>
  );
};

export default BookingPage;